/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Link, Typography } from '@coursera/cds-core';
import { Box, Container, ReadMore, color } from '@coursera/coursera-ui';
import { SvgChevronLeft } from '@coursera/coursera-ui/svg';

import CourseEnrollButton from 'bundles/enroll-common/components/CourseEnrollButton';
import XDPRating from 'bundles/enterprise-legacy-xdp/components/XDPRating';
import LearnerStory from 'bundles/reviews/components/LearnerStory';
import ReviewHighlights from 'bundles/reviews/components/ReviewHighlights';
import ReviewsContainer from 'bundles/reviews/components/ReviewsContainer';
import ReviewsPageSeoElements from 'bundles/reviews/components/ReviewsPageSeoElements';
import TopRatings from 'bundles/reviews/components/TopRatings';
import type { ReviewsPageSchemaReview } from 'bundles/reviews/hooks/useReviewsPageData';
import type { ReviewRouter } from 'bundles/reviews/types/reviewTypes';

import _t from 'i18n!nls/reviews';

import 'css!bundles/reviews/components/__styles__/ReviewsPage';

type Props = {
  slug: string;
  courseInfo: any;
  schemaReviews: Array<ReviewsPageSchemaReview>;
  router: ReviewRouter;
};

const styles = {
  backToCDPLink: () => css`
    color: var(--cds-color-neutral-primary);

    &:hover,
    &:focus {
      color: var(--cds-color-neutral-primary);
    }
  `,
  modifiedComfy: () => css`
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
  `,
  reviewsHeader: () => css`
    margin-bottom: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-600);
  `,
};

const NUM_TOP_REVIEWS_TO_SHOW = 2;

// Only exporting here for testing purposes, look at default export for normal use
export class ReviewsPage extends React.Component<Props> {
  filterFiveStarReviews = () => {
    const { router } = this.props;

    // this is for the link in top reviews, that says 'see 5 star reviews'
    router.replace({
      pathname: router.location.pathname,
      query: { star: '5', page: 1 },
    });
  };

  render() {
    const {
      courseInfo,
      courseInfo: {
        xdpMetadata: { cdpMetadata },
      },
      router,
    } = this.props;

    // the id from courseInfo is actually the xdpProductId, which is made up of
    // productType~productId (in other words, COURSE~<courseId> or SPECIALIZATION~<s12nId>)
    const courseId = courseInfo?.id?.split('~')[1] ?? '';
    const courseName = (courseInfo || {}).name;
    const topReviewsToDisplay = cdpMetadata.topReviews?.slice(0, NUM_TOP_REVIEWS_TO_SHOW);
    const programSlug = router.params.programSlug;

    return (
      <Container rootClassName="CDPReviewsPage ReviewsPage" data-e2e="course-reviews-page">
        <div className="CourseReviewTitle border-bottom">
          <Box flexDirection="column">
            <Link
              css={styles.backToCDPLink}
              href={programSlug ? `/programs/${programSlug}/learn/${courseInfo.slug}` : `/learn/${courseInfo.slug}`}
            >
              <Box alignItems="center" rootClassName="m-t-1">
                <SvgChevronLeft size={12} />
                {courseName && (
                  <div className="font-sm m-l-1">
                    <FormattedMessage message={_t('Back to {courseName}')} courseName={courseName} />
                  </div>
                )}
              </Box>
            </Link>
            <Typography variant="d2" css={styles.reviewsHeader} color="supportText">
              <FormattedMessage
                message={_t(`Learner Reviews & Feedback for {courseName} by {partnerName}`)}
                courseName={courseName}
                partnerName={cdpMetadata.partners[0].name}
              />
            </Typography>

            {!programSlug && (
              <CourseEnrollButton
                courseId={courseId}
                css={styles.modifiedComfy}
                showStartDate={true}
                enrollButtonText={_t('Enroll for free')}
              />
            )}

            {cdpMetadata.ratings && (
              <XDPRating
                averageFiveStarRating={cdpMetadata.ratings.averageFiveStarRating}
                ratingCount={cdpMetadata.ratings.ratingCount}
                commentCount={cdpMetadata.ratings.commentCount}
                rootClassName="m-y-2"
                a11yIdentifier="Reviews"
                ratingColor={color.primaryText}
              />
            )}
          </Box>
        </div>
        <div className="AboutCourse border-bottom">
          <Typography variant="d2" className="m-y-2">
            {_t('About the Course')}
          </Typography>
          <div className="m-b-2">
            <ReadMore lines={5} more={_t('View All')} less={_t('View Less')}>
              {cdpMetadata.description}
            </ReadMore>
          </div>
        </div>
        {/* Component to use for 'write a review' feature
        <ReviewOverview overview={reviewOverview} /> */}
        {cdpMetadata.reviewHighlights && cdpMetadata.reviewHighlights.length > 0 && (
          <ReviewHighlights highlights={cdpMetadata.reviewHighlights} />
        )}
        {cdpMetadata.testimonials && cdpMetadata.testimonials.length > 0 && (
          <LearnerStory story={cdpMetadata.testimonials.elements[0]} />
        )}
        {topReviewsToDisplay && topReviewsToDisplay.length > 0 ? (
          <TopRatings ratings={topReviewsToDisplay} onStarReviewsClick={() => this.filterFiveStarReviews()} />
        ) : null}
        <ReviewsContainer courseName={courseName} courseId={courseInfo.id} />
        <ReviewsPageSeoElements courseInfo={courseInfo} router={router} schemaReviews={this.props.schemaReviews} />
      </Container>
    );
  }
}

export default ReviewsPage;
